import { Fragment } from "react";
import { FiCheck, FiChevronRight } from "react-icons/fi";

export type WorkflowStage = 1 | 2 | 3;

const STAGES: { id: WorkflowStage; label: string }[] = [
    { id: 1, label: "Image" },
    { id: 2, label: "Dental" },
    { id: 3, label: "Video" },
];

interface Props {
    currentStage: WorkflowStage;
    onStageChange: (stage: WorkflowStage) => void;
    completedStages: Set<WorkflowStage>;
    canAccessStage: (stage: WorkflowStage) => boolean;
    upcomingStages?: Set<WorkflowStage>;
    /** Stage to draw attention to as the next step (e.g. Dental after Image). */
    highlightStage?: WorkflowStage | null;
}

export default function WorkflowStageStepper({
    currentStage,
    onStageChange,
    completedStages,
    canAccessStage,
    upcomingStages,
    highlightStage = null,
}: Props) {
    return (
        <div className="rounded-[14px] border border-white/[0.07] bg-[#212226] px-2.5 py-2 sm:px-3 sm:py-2.5 w-full sm:w-auto">
            <div className="flex items-center gap-0.5 sm:gap-1 overflow-x-auto ss-stepper-scroll">
                {STAGES.map((stage, index) => {
                    const isUpcoming = upcomingStages?.has(stage.id) ?? false;
                    const isActive = currentStage === stage.id;
                    const isComplete = completedStages.has(stage.id);
                    const isHighlight =
                        highlightStage === stage.id && !isActive && !isUpcoming;
                    const accessible = isUpcoming || canAccessStage(stage.id);

                    return (
                        <Fragment key={stage.id}>
                            <div className="relative group shrink-0">
                                <button
                                    type="button"
                                    disabled={!accessible}
                                    title={
                                        isUpcoming
                                            ? "Upcoming feature"
                                            : isHighlight
                                              ? `Continue to ${stage.label}`
                                              : stage.label
                                    }
                                    onClick={() =>
                                        accessible && onStageChange(stage.id)
                                    }
                                    className={`flex items-center rounded-[10px] px-2 py-1.5 sm:px-2.5 sm:py-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                                        isActive
                                            ? "bg-[#1a1b1e]"
                                            : isHighlight
                                              ? "bg-[#1DA8C6]/15 ring-1 ring-[#1DA8C6]/50"
                                              : "hover:bg-white/[0.03]"
                                    }`}
                                >
                                    <div className="flex items-center gap-1.5 sm:gap-2">
                                        <span
                                            className={`w-[22px] h-[22px] rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                                                isActive || isHighlight
                                                    ? `bg-[#1DA8C6] text-[#08151A] ${
                                                          isHighlight
                                                              ? "ss-stepper-next"
                                                              : ""
                                                      }`
                                                    : isComplete
                                                      ? "border border-[rgba(29,168,198,0.45)] bg-[rgba(29,168,198,0.16)] text-[#1DA8C6]"
                                                      : "border border-white/10 text-[#6B6C72]"
                                            }`}
                                        >
                                            {isComplete && !isActive ? (
                                                <FiCheck className="w-3.5 h-3.5" />
                                            ) : (
                                                stage.id
                                            )}
                                        </span>
                                        <span
                                            className={`text-[13px] whitespace-nowrap ${
                                                isActive || isHighlight
                                                    ? "font-semibold text-[#1DA8C6]"
                                                    : isComplete
                                                      ? "font-medium text-[#F1F0EA]"
                                                      : "font-medium text-[#6B6C72]"
                                            }`}
                                        >
                                            {stage.label}
                                        </span>
                                        {isUpcoming && (
                                            <span className="hidden sm:inline-flex items-center rounded-full border border-amber-400/25 bg-amber-400/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-300">
                                                Soon
                                            </span>
                                        )}
                                    </div>
                                </button>
                                {isUpcoming && (
                                    <span
                                        role="tooltip"
                                        className="pointer-events-none absolute bottom-[calc(100%+6px)] left-1/2 -translate-x-1/2 whitespace-nowrap rounded-[7px] border border-white/[0.14] bg-[#0D0E10] px-2.5 py-1.5 text-sm text-[#F1F0EA] opacity-0 shadow-[0_8px_20px_rgba(0,0,0,0.4)] transition-opacity duration-150 group-hover:opacity-100 z-50"
                                    >
                                        Upcoming feature
                                    </span>
                                )}
                            </div>
                            {index < STAGES.length - 1 && (
                                <FiChevronRight
                                    className={`w-4 h-4 shrink-0 mx-0.5 sm:mx-1 ${
                                        isActive || isHighlight
                                            ? "ss-stepper-arrow-active text-[#1DA8C6] stroke-[3]"
                                            : "text-[#6B6C72]"
                                    }`}
                                    aria-hidden
                                />
                            )}
                        </Fragment>
                    );
                })}
            </div>
        </div>
    );
}
